// Perfil público de outro usuário (modal) e suas conquistas.
// Extraído de App.js sem alteração de comportamento.
import { useState, useEffect } from "react";
import React from "react";
import ReactDOM from "react-dom";
import { sb } from "../lib/supabase";
import { useTheme } from "../lib/theme";
import { NavIcon } from "./icons";
import { ADMIN_EMAILS, BADGES, findContest } from "../lib/constants";
import { ContestEmblem } from "./ui";
import { dbGet } from "../lib/db";
import { fmtDateLong } from "../lib/dates";
import { useStudyDays, badgeExtra } from "../lib/stats";
import { calcBadges } from "./badges";
import { fetchNoteCounts } from "../lib/noteStats";

// ── COMUNIDADE ───────────────────────────────────────────────────────────────
// ProfileBadges: loads a user's data and renders their earned badges
function ProfileBadges({ userId, isOwn, isAdmin }) {
  const t = useTheme();
  const sdB = useStudyDays(isOwn ? userId : null, !!isAdmin);
  const [unlocked, setUnlocked] = React.useState(null);
  const [hoveredB, setHoveredB] = React.useState(null);

  React.useEffect(() => {
    if (!userId) return;
    async function load() {
      const [rv, simData, questData, nc] = await Promise.all([
        sb.from("ranking_view").select("total_seconds").eq("id", userId).single(),
        dbGet("simulados", userId, []),
        dbGet("questoes",  userId, []),
        fetchNoteCounts(userId),
      ]);
      const totalSec = rv?.data?.total_seconds || 0;
      if (isOwn) {
        if (!sdB.loaded) return;                           // espera o Calendário/sequência carregarem
        const b = badgeExtra(sdB, !!isAdmin, totalSec);
        setUnlocked(calcBadges(b.totalSec, simData, questData, { ...(b.extra || {}), nc }));
      } else {
        setUnlocked(calcBadges(totalSec, simData, questData, { nc }));
      }
    }
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- dependências mantidas como estavam (mudar alteraria quando o efeito roda)
  }, [userId, isOwn, isAdmin, sdB.loaded]);

  if (!unlocked) return <div style={{ fontSize:"0.72rem", color:t.textFaint }}>Carregando…</div>;

  const earned = BADGES.filter(b => unlocked.has(b.id));

  return (
    <div style={{ display:"flex", flexWrap:"wrap", gap:"0.4rem" }}>
      {earned.length === 0
        ? <p style={{ margin:0, fontSize:"0.75rem", color:t.textFaint }}>Nenhuma conquista ainda.</p>
        : earned.map(b => (
          <div key={b.id}
            onMouseEnter={()=>setHoveredB(b.id)} onMouseLeave={()=>setHoveredB(null)}
            style={{ position:"relative", width:36, height:36, borderRadius:8, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1.15rem",
              background:t.acDim, border:`1px solid ${t.ac}40`, cursor:"default", color:t.ac }}>
            <NavIcon id={"b_" + b.id} size={20} />
            {hoveredB===b.id && (
              <div style={{ position:"absolute", bottom:"110%", left:"50%", transform:"translateX(-50%)", background:t.bgCard, border:`1px solid ${t.border}`, borderRadius:7, padding:"0.4rem 0.7rem", whiteSpace:"nowrap", zIndex:200, pointerEvents:"none", boxShadow:"0 4px 16px #0008" }}>
                <p style={{ margin:0, fontSize:"0.72rem", fontWeight:700, color:t.ac }}>{b.label}</p>
                <p style={{ margin:0, fontSize:"0.62rem", color:t.textFaint }}>{b.desc}</p>
              </div>
            )}
          </div>
        ))
      }
    </div>
  );
}

export function UserProfileModal({ userId, currentUserId, onClose, adminEmail }) {
  const t = useTheme();
  const [profile,   setProfile]   = useState(null);
  const [notFound,  setNotFound]  = useState(false);
  const [bio,       setBio]       = useState("");
  const [editBio,   setEditBio]   = useState(false);
  const [savingBio, setSavingBio] = useState(false);
  const isOwn = userId === currentUserId;

  useEffect(() => {
    if (!userId) { setNotFound(true); return; }
    sb.from("profiles").select("id, public_name, target_contest, avatar_url, created_at, role, bio, banned, last_seen_at").eq("id", userId).single().then(({ data, error }) => {
      if (data) { setProfile(data); setBio(data.bio || ""); }
      else { setNotFound(true); }
    });
  }, [userId]);

  async function saveBio() {
    setSavingBio(true);
    await sb.from("profiles").update({ bio }).eq("id", userId);
    setSavingBio(false);
    setEditBio(false);
    setProfile(p => ({ ...p, bio }));
  }

  const modalContent = notFound ? (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.72)", backdropFilter:"blur(12px)", zIndex:99999, display:"flex", alignItems:"center", justifyContent:"center" }} onClick={onClose}>
      <div style={{ background:t.bgCard, border:`1px solid ${t.border}`, borderRadius:20, padding:"2rem", textAlign:"center", maxWidth:320, boxShadow:"0 24px 64px rgba(0,0,0,0.7)" }} onClick={e=>e.stopPropagation()}>
        <p style={{ color:t.textDim, fontSize:"0.95rem", margin:"0 0 1rem" }}>Perfil não encontrado.</p>
        <button onClick={onClose} style={{ background:t.ac, border:"none", borderRadius:8, padding:"0.5rem 1.5rem", color:t.mode==="dark"?"#07100d":"#fff", fontWeight:700, cursor:"pointer" }}>Fechar</button>
      </div>
    </div>
  ) : !profile ? (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.72)", backdropFilter:"blur(12px)", zIndex:99999, display:"flex", alignItems:"center", justifyContent:"center" }} onClick={onClose}>
      <div style={{ width:36, height:36, border:`3px solid ${t.ac}`, borderTopColor:"transparent", borderRadius:"50%", animation:"spin 0.8s linear infinite" }}/>
    </div>
  ) : (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.72)", backdropFilter:"blur(12px)", zIndex:99999, display:"flex", alignItems:"center", justifyContent:"center", padding:"1rem" }} onClick={onClose}>
      <div style={{ background:t.bgCard, border:`1px solid ${t.border}`, borderRadius:20, padding:"2rem", width:"100%", maxWidth:400, position:"relative", boxShadow:"0 24px 64px rgba(0,0,0,0.7)", backdropFilter:"blur(20px)" }} onClick={e=>e.stopPropagation()}>
        <button onClick={onClose} style={{ position:"absolute", top:12, right:14, background:"transparent", border:"none", color:t.textDim, fontSize:"1.1rem", cursor:"pointer" }}>✕</button>
        <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:"0.8rem", marginBottom:"1.2rem" }}>
          <div style={{ width:80, height:80, borderRadius:"50%", background:t.bgInner, border:`2px solid ${t.border}`, overflow:"hidden", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"2rem" }}>
            {profile.avatar_url
              ? <img src={profile.avatar_url} style={{ width:"100%", height:"100%", objectFit:"cover" }} alt="" />
              : <span style={{ color:t.textDim }}>{profile.public_name?.[0]?.toUpperCase()||"?"}</span>}
          </div>
          <div style={{ textAlign:"center" }}>
            <p style={{ margin:0, fontWeight:800, fontSize:"1.1rem", color:t.text }}>{profile.public_name}</p>
            {profile.target_contest && (() => {
                const known = findContest(profile.target_contest);
                if (known) return <p style={{ margin:"0.4rem 0 0", fontSize:"0.72rem", color:t.textFaint }}><span style={{ display:"inline-flex", alignItems:"center", gap:"0.3rem", verticalAlign:"middle" }}><ContestEmblem id={known.id} size={15} />{known.label}</span></p>;
                const raw = profile.target_contest;
                const si = raw.indexOf(" ");
                const emoji = si>0?raw.slice(0,si):"⭐", name = si>0?raw.slice(si+1):raw;
                return <p style={{ margin:"0.4rem 0 0", fontSize:"0.72rem", color:"#facc15" }}>{emoji} {name}</p>;
              })()}
            {profile.created_at && <p style={{ margin:"0.45rem 0 0", fontSize:"0.7rem", color:t.textFaint }}>Membro desde {fmtDateLong(profile.created_at)}</p>}
          </div>
        </div>
        <div style={{ background:t.bgInner, border:`1px solid ${t.border}`, borderRadius:10, padding:"0.8rem", marginBottom:"0.8rem" }}>
          <p style={{ margin:"0 0 0.4rem", fontSize:"0.65rem", textTransform:"uppercase", color:t.textDim, letterSpacing:"0.08em" }}>Sobre</p>
          {editBio ? (
            <>
              <textarea value={bio} onChange={e=>setBio(e.target.value)} rows={3}
                style={{ width:"100%", background:t.bgCard, border:`1px solid ${t.border}`, borderRadius:7, padding:"0.5rem", color:t.text, fontSize:"0.85rem", resize:"vertical", outline:"none", fontFamily:"inherit", boxSizing:"border-box" }}/>
              <div style={{ display:"flex", gap:"0.5rem", marginTop:"0.5rem" }}>
                <button onClick={saveBio} disabled={savingBio} style={{ background:t.ac, border:"none", borderRadius:7, padding:"0.4rem 1rem", color:t.mode==="dark"?"#07100d":"#fff", fontWeight:700, fontSize:"0.8rem", cursor:"pointer" }}>{savingBio?"Salvando…":"Salvar"}</button>
                <button onClick={()=>{setEditBio(false);setBio(profile.bio||"");}} style={{ background:"transparent", border:`1px solid ${t.border}`, borderRadius:7, padding:"0.4rem 0.8rem", color:t.textDim, fontSize:"0.8rem", cursor:"pointer" }}>Cancelar</button>
              </div>
            </>
          ) : (
            <>
              <p style={{ margin:0, fontSize:"0.85rem", color:profile.bio?t.text:t.textFaint, lineHeight:1.6 }}>{profile.bio||"Sem descrição ainda."}</p>
              {isOwn && <button onClick={()=>setEditBio(true)} style={{ background:"transparent", border:"none", color:t.ac, fontSize:"0.75rem", cursor:"pointer", padding:"0.3rem 0", marginTop:"0.3rem", display:"inline-flex", alignItems:"center", gap:"0.35rem", fontFamily:"inherit" }}><NavIcon id="editar" size={13} />Editar descrição</button>}
            </>
          )}
        </div>
        <div style={{ background:t.bgInner, border:`1px solid ${t.border}`, borderRadius:10, padding:"0.8rem" }}>
          <p style={{ margin:"0 0 0.6rem", fontSize:"0.65rem", textTransform:"uppercase", color:t.textDim, letterSpacing:"0.08em" }}>Conquistas</p>
          <ProfileBadges userId={userId} isOwn={isOwn} isAdmin={isOwn && ADMIN_EMAILS.includes(adminEmail)} />
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
}
